import { useStore } from '../store/StoreContext.jsx';
import { getOwnerNameOptions, getTotalIncome, formatDollars, isRentalProperty } from '../store/selectors.js';
import { useAutoSourceSync } from '../store/useAutoSourceSync.js';
import DataTable from './DataTable.jsx';
import { SectionShell } from './FormSection.jsx';
import { SF_TEXT_LIMITS } from '../schema/fieldLimits.js';

function fullName(first, last) {
  return [(first || '').trim(), (last || '').trim()].filter(Boolean).join(' ');
}

// Only the owner is locked on a derived Employment Income row. Annual Amount is
// entered directly in this table (the Annual Earnings box that used to drive it
// was removed 2026-07-14). Retirement Date
// used to be locked too, which is what stopped advisors reordering these rows
// and left the derived row showing a date picker the hand-added rows did not
// have: "I would just make this an open field, they could type whatever they
// want in there" (Jake, 2026-08-05). It still seeds from the Employment section
// until the advisor sets their own value (seedWhileEmpty below).
const EMP_READONLY_COLS = ['owner'];
// notes is deliberately NOT read-only: "From assets" is only a seed the advisor
// can overwrite (seedFields below keeps their edit from being re-stomped).
const RE_INCOME_READONLY_COLS = ['owner', 'description', 'annualAmount'];

// Full-year vs partial-year indicator (meeting 7/6). Flags income that doesn't
// span the whole year — a mid-year retirement, a new job, a one-off — so cash
// flow can be read correctly. Shared across every income table; `detail: true`
// keeps it in the per-row panel so it never crowds the primary money columns.
// No Salesforce field yet (pending Srivani, like pension startAge) — it sends
// on save and lights up when the field lands.
const yearTypeCol = {
  key: 'yearType', label: 'Full/Partial Yr', type: 'select',
  options: ['Full Year', 'Partial Year'],
  labelInfo: 'Does this income run the full year, or only part of it (e.g. a mid-year retirement or new job)?',
  detail: true,
};

export default function IncomeSection({ section, num }) {
  const { data } = useStore();
  const ownerOptions = getOwnerNameOptions(data);

  // Employment Income: one starter auto-row per working client. Both Employed
  // and Self Employed clients get one (self-employed people have business
  // income). dropOrphans:false keeps hand-added rows regardless of employment
  // status; a derived row whose client stops working is removed by the merge
  // (tagged-orphan rule) so phantom income can't flow to Salesforce.
  useAutoSourceSync({
    targetPath: 'income.employment',
    matchKeyField: '_source',
    dropOrphans: false,
    // Description and Retirement Date are seeds, not mirrors: each keeps
    // tracking its source in the Employment section until the advisor types
    // their own value, after which they own it and a re-merge won't stomp it
    // (they can write "1099 income" over the employer name and keep it).
    // Clearing a cell puts it back to following the source. Note this is
    // tracked by remembering the last value seeded, NOT by testing whether the
    // cell is non-empty: the source is typed one character at a time and this
    // sync runs on every keystroke, so "non-empty means the advisor owns it"
    // froze the description on the employer's first letter. Reclaim identity is
    // owner/uid, so editing either cell can't orphan the row.
    seedWhileEmpty: ['description', 'retirementDate'],
    // After a Salesforce load the _source is gone; the merge reclaims by the
    // deterministic row uid first (survives renames), then by owner name as a
    // fallback for rows saved before deterministic uids existed.
    naturalKey: r => {
      const o = (r?.owner || '').trim().toLowerCase();
      return o ? 'emp:' + o : '';
    },
    computeAutoRows: d => {
      const fam = d?.family || {};
      const flags = d?._flags || {};
      const emp = d?.employment || {};
      const rows = [];
      // Deterministic, household-scoped row uid. Row uids survive the
      // Salesforce round-trip (unlike _source), so the merge can reclaim the
      // same row after a reload even if the owner was renamed — without this,
      // renaming a spouse duplicated their income row on every reload (UAT).
      const hh = String(d?._uid || '').trim();
      const stableUid = role => (hh ? { _uid: `${hh}-inc-${role}` } : {});
      // Status defaults to 'Employed' if not yet persisted (matches the UI).
      // `||` so a stored empty string defaults too, matching the Employment
      // section — otherwise the derived income row silently vanishes.
      const c1Status = emp.client1?.status || 'Employed';
      const c2Status = emp.client2?.status || 'Employed';
      const isWorking = s => s === 'Employed' || s === 'Self Employed';
      if (isWorking(c1Status)) {
        rows.push({
          _source: 'client1',
          ...stableUid('client1'),
          owner: fullName(fam.client1FirstName, fam.client1LastName),
          description: emp.client1?.employer || '',
          // annualAmount is intentionally omitted: the advisor types it in this
          // table, and leaving it out of the auto-row means a re-merge preserves
          // their entry instead of overwriting it.
          retirementDate: emp.client1?.retirementDate || '',
          _readOnly: EMP_READONLY_COLS,
        });
      }
      if (flags.hasSpouse && isWorking(c2Status)) {
        rows.push({
          _source: 'client2',
          ...stableUid('client2'),
          owner: fullName(fam.client2FirstName, fam.client2LastName),
          description: emp.client2?.employer || '',
          retirementDate: emp.client2?.retirementDate || '',
          _readOnly: EMP_READONLY_COLS,
        });
      }
      return rows;
    },
  });

  // Other Income: one auto-row per real-estate asset with rental income.
  // dropOrphans:false so a hand-added Other Income row isn't deleted the moment
  // the user types a description (its description made it look auto-sourced).
  // The RE-rental auto-rows are still reclaimed by description on reload, so an
  // existing rental doesn't duplicate.
  useAutoSourceSync({
    targetPath: 'income.other',
    matchKeyField: '_source',
    dropOrphans: false,
    // Notes is a seed ("From assets"), not a derived value — advisor-editable.
    seedFields: ['notes'],
    // After a Salesforce load the _source is gone; reclaim the loaded RE-rental
    // row by its description so it isn't duplicated.
    naturalKey: r => {
      const d = (r?.description || '').trim().toLowerCase();
      return d ? 're:' + d : '';
    },
    computeAutoRows: d => {
      const re = Array.isArray(d?.assets?.realEstate) ? d.assets.realEstate : [];
      // Same deterministic-uid scheme as Employment Income above: identity
      // survives the round-trip even if the property description is renamed.
      const hh = String(d?._uid || '').trim();
      return re
        // Any amount in Income (EBT) makes it a rental, including a NEGATIVE
        // one. A property running at a loss has to be able to reach the income
        // table as a negative line: "then the loss should flow to the income
        // section... it's not an expense, it's just a negative" (Jake,
        // 2026-08-05). The old filter required > 0, so a loss-making rental
        // produced no row at all while its mortgage still hit expenses.
        .filter(r => isRentalProperty(r))
        .map((r, i) => {
          // EVERY income-producing property gets an income row. Requiring
          // _autoKey dropped manually-added (+ Add) properties — only the
          // three checkbox rows carry it, so "3 rentals in, 1 income row out"
          // (UAT: Eugene, 7/8). Checkbox rows keep their legacy re:ownHome-
          // style keys (existing saved uids still reclaim); manual rows key
          // off their own row uid.
          const key = r._autoKey || r._uid || `row${i}`;
          return {
            _source: `re:${key}`,
            ...(hh ? { _uid: `${hh}-inc-${key}` } : {}),
            owner: r.ownership || '',
            description: r.description || 'Asset Income',
            annualAmount: parseFloat(r.incomeEBT) * 12,
            notes: 'From assets',
            _readOnly: RE_INCOME_READONLY_COLS,
          };
        });
    },
  });

  const tables = [
    {
      key: 'employment',
      reorderable: true,
      title: 'Employment Income',
      columns: [
        { key: '_source', label: '', hidden: true },
        { key: 'owner', label: 'Owner', type: 'datalist', options: ownerOptions, maxLength: SF_TEXT_LIMITS['PQ_Income__c.Owner__c'] },
        { key: 'description', label: 'Description', colClass: 'col-wide' },
        { key: 'annualAmount', label: 'Annual Amount', type: 'currency' },
        { key: 'retirementDate', label: 'Retirement Date', type: 'date', dateRange: 'retirement' },
        { key: 'cola', label: 'COLA %', type: 'percent' },
        yearTypeCol,
      ],
    },
    {
      key: 'socialSecurity',
      reorderable: true,
      title: 'Social Security',
      columns: [
        { key: 'owner', label: 'Owner', type: 'datalist', options: ownerOptions, maxLength: SF_TEXT_LIMITS['PQ_Income__c.Owner__c'] },
        { key: 'startingAge', label: 'Starting Age', type: 'number', min: 0, max: 120, integer: true },
        { key: 'annualAmount', label: 'Annual Amount', type: 'currency' },
        { key: 'fullRetAge', label: 'Full Ret. Age', type: 'number', min: 0, max: 120, integer: true },
        { key: 'cola', label: 'COLA %', type: 'percent' },
        yearTypeCol,
      ],
    },
    {
      key: 'pension',
      reorderable: true,
      title: 'Pension',
      columns: [
        { key: 'owner', label: 'Owner', type: 'datalist', options: ownerOptions, maxLength: SF_TEXT_LIMITS['PQ_Income__c.Owner__c'] },
        // Clients often have more than one pension (e.g. "Howard's VA pension"),
        // and the row was unlabelled without this (Jake, 2026-07-23). The field
        // already round-tripped through Salesforce; only the column was missing.
        { key: 'description', label: 'Description', colClass: 'col-wide' },
        { key: 'startDate', label: 'Start Date', type: 'date', dateRange: 'planning' },
        { key: 'startAge', label: 'Starting Age', type: 'number', min: 0, max: 120, labelInfo: 'Age the pension begins — some clients think in age rather than a date.', integer: true },
        { key: 'annualAmount', label: 'Annual Amount', type: 'currency' },
        { key: 'survivorBenefit', label: 'Survivor Benefit', type: 'percent' },
        { key: 'cola', label: 'COLA %', type: 'percent' },
        yearTypeCol,
      ],
    },
    {
      key: 'other',
      reorderable: true,
      title: 'Other Income',
      // detail: true = demoted to the per-row panel when squeezed (R2).
      columns: [
        { key: '_source', label: '', hidden: true },
        { key: 'owner', label: 'Owner', type: 'datalist', options: ownerOptions, maxLength: SF_TEXT_LIMITS['PQ_Income__c.Owner__c'] },
        { key: 'description', label: 'Description', colClass: 'col-wide' },
        { key: 'annualAmount', label: 'Annual Amount', type: 'currency' },
        { key: 'startDate', label: 'Start Date', type: 'date', dateRange: 'planning' },
        { key: 'endDate', label: 'End Date', type: 'date', dateRange: 'planning', notBefore: 'startDate', detail: true },
        { key: 'cola', label: 'COLA %', type: 'percent', detail: true },
        yearTypeCol,
        // notes moved to the shared per-row note icon (DataTable).
      ],
    },
  ];

  return (
    <SectionShell section={section} num={num}>
      {tables.map(t => (
        <DataTable key={t.key} sectionId="income" tableDef={t} />
      ))}
      <div className="grid" style={{ marginTop: 14 }}>
        <label className="field f-3 readonly">
          <div className="label-row">
            <span className="label">Total Income</span>
          </div>
          <input
            type="text"
            className="num"
            value={formatDollars(getTotalIncome(data))}
            readOnly
          />
        </label>
      </div>
    </SectionShell>
  );
}
